// ===========================================================================
// Impersonation guard for user-chosen names (display handles AND group-room
// names). Someone calling themselves "Lumi Admin" or opening a room called
// "Admin chat" can talk a 13-year-old into anything, so those names are refused
// outright -- staff identity is granted by the server (the verified staff
// badge), never typed by a user.
//
// The matching is deliberately aggressive about DISGUISES, because the whole
// point of the trick is to look official while slipping past a naive filter:
//   * case, spacing, punctuation, and repeats normalize away
//     ("L u m i", "l-u-m-i", "lumiii" -> "lumi")
//   * leetspeak folds back ("1um1", "4dmin" -> "lumi", "admin")
//   * confusable letters from other alphabets fold to ASCII (Cyrillic a,
//     Greek omicron), which is how most real impersonation is done
//   * Unicode NFKD folds fullwidth forms and accents
//
// A name is refused when it contains a staff word, or pairs the brand with an
// authority word. Ordinary names that merely CONTAIN a short word ("Modest",
// "Ceo Ramos", "staffordshire terrier") survive, because matching is on whole
// normalized words. Source is ASCII only: lookalikes are written as escapes.
// ===========================================================================

/** Confusable characters folded to their ASCII lookalike. */
const CONFUSABLES = {
  '\u0430': 'a', // cyrillic a
  '\u04d3': 'a', // cyrillic a with diaeresis
  4: 'a',
  '@': 'a',
  '\u044c': 'b', // cyrillic soft sign
  8: 'b',
  6: 'b',
  '\u0441': 'c', // cyrillic es
  '(': 'c',
  '<': 'c',
  '\u0501': 'd', // cyrillic komi de
  '\u0435': 'e', // cyrillic ie
  '\u0451': 'e', // cyrillic io
  3: 'e',
  '\u20ac': 'e', // euro sign
  '\u0261': 'g', // latin script g
  9: 'g',
  '\u04bb': 'h', // cyrillic shha
  '\u0456': 'i', // cyrillic byelorussian-ukrainian i
  '\u0457': 'i', // cyrillic yi
  1: 'i',
  '!': 'i',
  '|': 'i',
  '\u0458': 'j', // cyrillic je
  '\u043a': 'k', // cyrillic ka
  '\u04cf': 'l', // cyrillic palochka
  '\u043c': 'm', // cyrillic em
  '\u0438': 'n', // cyrillic i
  '\u043e': 'o', // cyrillic o
  '\u03bf': 'o', // greek omicron
  0: 'o',
  '\u0440': 'p', // cyrillic er
  '\u0433': 'r', // cyrillic ghe
  '\u0455': 's', // cyrillic dze
  5: 's',
  $: 's',
  '\u0442': 't', // cyrillic te
  7: 't',
  '+': 't',
  '\u0446': 'u', // cyrillic tse
  '\u03bd': 'v', // greek nu
  '\u0448': 'w', // cyrillic sha
  '\u0445': 'x', // cyrillic ha
  '\u0443': 'y', // cyrillic u
  '\u1d22': 'z', // small capital z
  2: 'z',
};

// Repeated letters are collapsed so "adminnn" reads as "admin" -- which also
// turns "support" into "suport" and "staff" into "staf". The word lists must go
// through the SAME collapse or they would never match their own words.
const collapse = (w) => w.replace(/(.)\1{1,}/g, '$1');

/** Brand words: the service itself. */
const BRAND = ['lumi', 'lumichat'];

/** Words that claim staff standing on their own. */
const STAFF = [
  'admin', 'administrator', 'moderator', 'sysadmin', 'sysop', 'staff',
  'support', 'helpdesk', 'webmaster', 'official', 'verified', 'security',
  'trustandsafety', 'owner', 'founder', 'operator', 'helpline',
  'police', 'ncmec', 'lumistaff', 'lumiteam', 'lumiadmin', 'lumisupport',
  // The voice of the service itself. These were only refused NEXT TO the brand
  // (they are in AUTHORITY), so they were perfectly usable names -- and the
  // group transcript decided a message was a system notice by comparing the
  // sender's handle to the string 'system'. The renderer no longer works that
  // way, but a person announcing themselves as the service is a lie whatever
  // it is styled like. ('system' and 'server' are in STAFF_EXACT instead: they
  // hide inside ordinary words.)
  'sysmsg', 'notification', 'announcement',
];

/** Exact words that are a staff claim, but too short to scan for inside a run. */
const STAFF_EXACT = [
  'mod', 'ceo', 'cto', 'coo', 'fbi', 'team', 'safety', 'guardian', 'helper',
  // Exact-match only. Scanning for these INSIDE a run would refuse "Observer"
  // (contains "server") and "Systematic Sue" (contains "system"), which are
  // ordinary names -- and a filter that eats ordinary names is its own failure.
  // As exact words they still catch "system", "Server" and the separator trick
  // "s y s t e m", which collapses to exactly the term.
  'system', 'server',
];

/** Authority words that are only a problem NEXT TO the brand. */
const AUTHORITY = [...STAFF, ...STAFF_EXACT, 'help', 'service', 'bot', 'system', 'info', 'hq', 'inc'];

/**
 * Reduce a name to comparable words: fold confusables and accents to ASCII,
 * lowercase, collapse repeated letters, and split on anything that is not a
 * letter. Separators are how "l.u.m.i" hides, so the letters are ALSO returned
 * joined into one run for a second pass.
 */
export function normalizeName(input) {
  const raw = String(input || '').toLowerCase();
  let folded = '';
  for (const ch of raw) {
    folded += Object.prototype.hasOwnProperty.call(CONFUSABLES, ch) ? CONFUSABLES[ch] : ch;
  }
  // NFKD splits fullwidth forms and accented letters apart; drop the combining
  // marks that leaves behind.
  folded = folded.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  // Fold a second time: NFKD can expose digits (fullwidth 4 -> 4) that are
  // themselves confusables.
  let second = '';
  for (const ch of folded) {
    second += Object.prototype.hasOwnProperty.call(CONFUSABLES, ch) ? CONFUSABLES[ch] : ch;
  }
  const words = second
    .split(/[^a-z]+/)
    .filter(Boolean)
    // "lumiii" and "adminnn" collapse to "lumi" and "admin".
    .map((w) => w.replace(/(.)\1{1,}/g, '$1'));
  return { words, joined: words.join('') };
}

/**
 * Whether a user-chosen name may impersonate Lumi or its staff.
 * @param {string} input
 * @returns {{ blocked: boolean, reason?: string }}
 */
export function checkImpersonation(input) {
  const { words, joined } = normalizeName(input);
  if (!joined) return { blocked: false };

  const hasWord = (list) => {
    const set = list.map(collapse);
    return words.some((w) => set.includes(w));
  };
  // The separator trick ("l u m i a d m i n") leaves no useful words, so also
  // look for a term anywhere inside the joined letters. Only terms long enough
  // that an innocent name is unlikely to contain them.
  const runHas = (list) => list.map(collapse).some((t) => t.length >= 5 && joined.includes(t));
  // ...and a short title spelled out with separators ("M o d") collapses to
  // exactly that word once joined.
  const runIs = (list) => list.map(collapse).includes(joined);

  if (hasWord(STAFF) || hasWord(STAFF_EXACT) || runHas(STAFF) || runIs(STAFF_EXACT)) {
    return { blocked: true, reason: 'staff_title' };
  }
  // The brand can appear in a nickname ("Lumi fan Katie"), but not paired with
  // authority, and not standing alone as the service itself.
  if (hasWord(BRAND) || runHas(BRAND)) {
    if (hasWord(AUTHORITY) || runHas(AUTHORITY)) return { blocked: true, reason: 'brand_authority' };
    if (words.length === 1 && BRAND.map(collapse).includes(words[0])) {
      return { blocked: true, reason: 'brand_alone' };
    }
  }
  return { blocked: false };
}

/** What to show whoever picked the name. */
export const IMPERSONATION_MESSAGE =
  'Please choose a different name. It looks like Lumi staff. Real Lumi staff always carry a verified badge that cannot be typed.';

export default { normalizeName, checkImpersonation, IMPERSONATION_MESSAGE };
